// CalendarStateContext.tsx — verbindlicher State-Contract des Kalender-Neubaus
// (`/termine`). Definiert View/Navigation/Filter/Suche/Geteilte-Ansicht und
// referenziert die Kalenderauswahl aus dem MailContext (single source of truth,
// NICHT dupliziert). localStorage-Persistenz von `view` und `filter`.
//
// SP1 baut View/Navigation/Filter-State/Suche-Debounce voll funktionsfähig.
// Die Event-Felder (`events`/`filteredEvents`/`metaById`/`reloadEvents`/
// `requestReload`) sind hier bereits als Contract reserviert (leer/No-Op) —
// SP5 füllt sie. SP2 nutzt `requestReload()` nach dem Speichern (statt eines
// parallelen CustomEvent-Mechanismus). So bauen SP2/SP5/SP6 gegen einen
// stabilen Typ (Konsistenz-Review A3/A4).

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import moment from "moment";
import "../../utils/momentDe"; // zentrale Locale-Quelle (de, Montag-Wochenstart, SP1)
import { useMailContext } from "../../context/MailContext";
import type { MailboxAccess } from "../../services/graphMailService";
import { useRealtime } from "../../context/RealtimeContext";
import type { CalendarRealtimePayload } from "../../services/realtimeService";
import { type Zeitskala, ZEITSKALA_KEY, readStoredZeitskala } from "./zeitskala";
import {
    calendarService,
    invalidateCalendarMailbox,
    peekCalendarEvents,
    type KontaktTerminEvent,
    type SourcedCalendarEvent,
} from "../../services/calendarService";
import { calendarTerminService } from "../../services/calendarTerminService";
import { toGraphDate } from "../dashboard/DashboardShared";
import { istGeburtstagEvent } from "./eventHeuristics";
import { classifyTermin, isVisibleUnderFilter } from "./types";
import type { CalendarGridEvent, CalendarMeta } from "./types";
import { graphAllDayDate } from "./allDay";
import { deriveViewEvents } from "./dedupe";

// Geburtstage gehören NICHT in den Kalender (TER-D4) → beim Event-Load defensiv
// herausgefiltert (istGeburtstagEvent). Heuristik jetzt zentral in
// `eventHeuristics.ts` (SP1), damit Kalender und Dashboard-Termine-Karte EINE
// Wahrheit teilen — Verhalten unverändert.

/** Sidecar-Meta-Key — identisch zu OutlookTermine (mailbox.toLowerCase():eventId). */
const metaKey = (mailbox: string, eventId: string): string =>
    `${mailbox.toLowerCase()}:${eventId}`;

/**
 * Quellfarbe der Ebene „Termine meiner Kontakte" — ein gedecktes Grau, damit
 * die fremden Termine hinter den eigenen zurücktreten (Rahmen gestrichelt,
 * siehe `.cal-ev-kontakt` in calendar.css).
 */
export const KONTAKT_EBENE_FARBE = "#8c8391";

/** Eine Kalenderquelle (Auswahl × Farbe), Basis für Event-Load + Spalten. */
export interface CalendarSource {
    address: string;
    displayName: string;
    color: string;
}

export type CalendarView = "day" | "week" | "month";
// Hinweis: 'agenda' (mobil) und 'workweek' sind NICHT im Enum (Arbeitswoche
// verworfen). Mobile-'agenda' kommt in SP6 als Sub-Modus, nicht als View-Wert
// → Enum bleibt stabil für SP2–SP5.

const ALL_VIEWS: CalendarView[] = ["day", "week", "month"];

export interface CalendarFilterState {
    kundentermin: boolean; // default true
    austausch: boolean; // default true
    sonstige: boolean; // default true (PO 21.09.2026)
    /**
     * K9 — „Nur Termine mit Vorgang" (Default false). Anders als die drei
     * anderen Flags ist das eine EINSCHRÄNKUNG: an ⇒ alles ohne Vorgang
     * verschwindet. Block/Abwesenheit bleiben unberührt sichtbar.
     */
    nurMitVorgang: boolean;
    /**
     * Ebene „Termine meiner Kontakte" (PO 28.09.2026, Default false): blendet
     * Termine aus Postfächern OHNE Leserecht ein, an denen ein Kontakt
     * beteiligt ist, für den die Nutzerin FO oder BO ist. Nur ansehen.
     */
    kontaktTermine: boolean;
}

export interface CalendarStateContextValue {
    // --- View & Navigation (SP1 voll funktionsfähig) ---
    view: CalendarView;
    setView: (v: CalendarView) => void;
    currentDate: Date;
    setCurrentDate: (d: Date) => void;
    goToToday: () => void;
    goPrev: () => void; // -1 Tag/Woche/Monat je nach view
    goNext: () => void; // +1 Tag/Woche/Monat je nach view

    // --- Filter (SP1: State + Persistenz; Anwendung auf Events erst SP5) ---
    filter: CalendarFilterState;
    setFilter: (f: CalendarFilterState) => void;
    toggleFilter: (key: keyof CalendarFilterState) => void;

    // --- Suche (SP1: State + debounce; Backend-Anbindung SP5) ---
    search: string;
    setSearch: (s: string) => void;
    debouncedSearch: string; // 300ms debounced; SP5 nutzt es als $search

    // --- Geteilte Ansicht (SP1: State; N-Spalten-Render SP5) ---
    shared: boolean;
    setShared: (b: boolean) => void;

    // --- Zeitskala des Tag-/Wochenrasters (T-31, persistiert) ---
    /** 15 / 30 / 60 Minuten; steuert Slot-Länge (Einrasten) und Stundenhöhe. */
    zeitskala: Zeitskala;
    setZeitskala: (z: Zeitskala) => void;

    // --- Single source of truth für Kalenderauswahl: REFERENZ auf MailContext ---
    selectedCalendars: string[];
    toggleCalendar: (address: string) => void;
    calendarColor: (address: string) => string;

    // --- abgeleiteter sichtbarer Range (Event-Load SP5/SP3) ---
    visibleRange: { from: Date; to: Date };

    // --- Events: in SP1 RESERVIERT (leer/No-Op); SP5 füllt sie (Review A3/A4) ---
    /** Alle geladenen View-Events (Overlay-dedupet, Entscheidung 62). SP5 lädt
     *  via listEventsForMailboxes. */
    events: CalendarGridEvent[];
    /** Auf Filter (kundentermin/austausch) gefilterte Events (SP5),
     *  Overlay-dedupet — für die Einzel-/Overlay-Ansicht. */
    filteredEvents: CalendarGridEvent[];
    /** Wie filteredEvents, aber OHNE Overlay-Dedupe — für die geteilte
     *  Spalten-Ansicht (SharedColumnsView): jede Spalte zeigt ihr Postfach
     *  VOLLSTÄNDIG (Review-Fix zu #784; Entscheidung 62 gilt nur fürs Overlay). */
    filteredColumnEvents: CalendarGridEvent[];
    /** Key `mailbox.toLowerCase():eventId` → Sidecar-Meta (SP5, via metaBatch). */
    metaById: Record<string, CalendarMeta>;
    /** Läuft irgendein Event-Load? (SP5; = initialLoading || revalidating). */
    loadingEvents: boolean;
    /**
     * Erst-Load OHNE Cache-Treffer und ohne bereits sichtbare Events (SP4):
     * das Grid ist leer → Skeleton/Spinner zeigen.
     */
    initialLoading: boolean;
    /**
     * Hintergrund-Refresh bei Cache-Treffer / bereits sichtbaren Events (SP4):
     * NICHT das Grid leeren — nur einen dezenten „Aktualisiere…"-Hinweis zeigen.
     */
    revalidating: boolean;
    /** Lädt die Events für den sichtbaren Range neu (SP5). */
    reloadEvents: () => Promise<void>;
    /** Reload-Brücke nach Save (SP2 ruft sie; SP5 verdrahtet sie an reloadEvents). */
    requestReload: () => void;
    /** Die aktiven Kalenderquellen (Auswahl × Farbe), Basis für Spalten (SP5). */
    calendarSources: CalendarSource[];
}

// localStorage-Keys (App-Muster, vgl. MailContext.SELECTED_CALENDARS_KEY).
const CAL_VIEW_KEY = "calendar.view"; // 'day' | 'week' | 'month'
const CAL_FILTER_KEY = "calendar.filter"; // JSON CalendarFilterState
// Zeitskala: ZEITSKALA_KEY ("calendar.zeitskala") in zeitskala.ts.
// shared/search werden NICHT persistiert (transient).
// selectedCalendars liegt bereits in MailContext (calendar.selectedCalendars).

const DEFAULT_VIEW: CalendarView = "day";
const DEFAULT_FILTER: CalendarFilterState = {
    kundentermin: true,
    austausch: true,
    sonstige: true,
    nurMitVorgang: false,
    kontaktTermine: false,
};

// Einheitliches `top` für den Overlay-/Grid-Load: MUSS mit dem Peek für die
// Warm/Cold-Erkennung (Erst-Load vs. Revalidierung) übereinstimmen, sonst
// verfehlt der Peek den Cache-Key (SP4).
const CAL_OVERLAY_TOP = 200;

/** Persistierten View validieren (alte 'agenda'/'workweek' → Default). */
function readStoredView(): CalendarView {
    try {
        const raw = localStorage.getItem(CAL_VIEW_KEY);
        if (raw && (ALL_VIEWS as string[]).includes(raw)) return raw as CalendarView;
    } catch {
        // ignore
    }
    return DEFAULT_VIEW;
}

function readStoredFilter(): CalendarFilterState {
    try {
        const raw = localStorage.getItem(CAL_FILTER_KEY);
        if (!raw) return { ...DEFAULT_FILTER };
        const parsed = JSON.parse(raw);
        // ACHTUNG: das hier ist eine WHITELIST. Ein neues Flag, das hier fehlt,
        // fällt beim Lesen aus dem localStorage stillschweigend weg.
        return {
            kundentermin: typeof parsed?.kundentermin === "boolean" ? parsed.kundentermin : true,
            austausch: typeof parsed?.austausch === "boolean" ? parsed.austausch : true,
            // PO 21.09.2026 — wer einen gespeicherten Filter von VOR dieser
            // Änderung hat, kennt das Flag nicht: dann `true`, sonst wären die
            // Sonstige-Termine bei jedem Bestandsnutzer einmalig unsichtbar.
            sonstige: typeof parsed?.sonstige === "boolean" ? parsed.sonstige : true,
            nurMitVorgang:
                typeof parsed?.nurMitVorgang === "boolean" ? parsed.nurMitVorgang : false,
            // PO 28.09.2026 — zuschaltbare Ebene, Default aus.
            kontaktTermine:
                typeof parsed?.kontaktTermine === "boolean" ? parsed.kontaktTermine : false,
        };
    } catch {
        return { ...DEFAULT_FILTER };
    }
}

/** moment-Einheit zur View (Navigations-Schrittweite). */
const unitForView = (v: CalendarView): moment.unitOfTime.DurationConstructor =>
    v === "day" ? "day" : v === "week" ? "week" : "month";

/** Sichtbaren Range aus view+currentDate ableiten (Event-Load SP5). */
function computeVisibleRange(view: CalendarView, date: Date): { from: Date; to: Date } {
    const m = moment(date);
    if (view === "day") {
        return { from: m.clone().startOf("day").toDate(), to: m.clone().endOf("day").toDate() };
    }
    if (view === "week") {
        // Deutsche Woche beginnt montags (isoWeek).
        return {
            from: m.clone().startOf("isoWeek").toDate(),
            to: m.clone().endOf("isoWeek").toDate(),
        };
    }
    // Monat: ein Tag Puffer vorne/hinten (Raster zeigt Rand-Tage).
    return {
        from: m.clone().startOf("month").subtract(1, "day").startOf("day").toDate(),
        to: m.clone().endOf("month").add(1, "day").endOf("day").toDate(),
    };
}

// ── Reload-Brücke für out-of-tree-Aufrufer (SP2) ──────────────────────────
// Das Termin-Fenster (TerminFormWindow) lebt im globalen WindowLayer AUSSERHALB
// des CalendarStateProvider-Trees und kann den Context daher nicht via Hook
// konsumieren. Statt eines parallelen window-CustomEvent-Mechanismus (Review A4)
// besitzt der Context selbst eine modul-lokale Subscriber-Liste: der Provider
// abonniert sie (→ requestReload), das Fenster ruft `requestCalendarReload()`
// nach dem Speichern. EINE Reload-Quelle, vom Context kontrolliert.
const reloadSubscribers = new Set<() => void>();

/**
 * Stößt einen Kalender-Reload an, ohne den Context per Hook konsumieren zu
 * müssen. Nutzt das TerminFormWindow (außerhalb des Provider-Trees) nach Save.
 * In SP1 ist der Provider-Reload ein No-Op (SP5 füllt reloadEvents).
 */
export function requestCalendarReload(): void {
    for (const fn of reloadSubscribers) {
        try {
            fn();
        } catch {
            // ein fehlerhafter Subscriber darf die übrigen nicht blockieren
        }
    }
}

const CalendarStateContext = createContext<CalendarStateContextValue | undefined>(undefined);

/**
 * K6 — der Provider ist parametrisierbar, ohne dass die Route `/termine` etwas
 * davon merkt: ohne Props verhält er sich Zeile für Zeile wie bisher.
 *
 * `auswahlIsoliert` ist der wichtige Schalter. Läuft der Kalender als FENSTER
 * über einer Mail oder einem Vorgang, darf ein Häkchen in der Kalenderauswahl
 * NICHT die persistierte Auswahl der Seite `/termine` umschreiben. Im
 * isolierten Modus hält der Provider `selectedCalendars` deshalb in eigenem
 * State (Startwert: `initialCalendars` bzw. die aktuelle MailContext-Auswahl),
 * `toggleCalendar` mutiert nur diesen State, und `view` wird nicht in den
 * localStorage geschrieben.
 */
export interface CalendarStateProviderProps {
    children: React.ReactNode;
    /** Kalenderauswahl + View lokal halten (Fenster-Modus). */
    auswahlIsoliert?: boolean;
    /** Startauswahl im isolierten Modus (Default: die MailContext-Auswahl). */
    initialCalendars?: string[];
    initialDate?: Date;
    initialView?: CalendarView;
    /** `top` des Graph-Loads. Default 200 — muss zum Peek-Cache-Key passen. */
    eventTop?: number;
}

export const CalendarStateProvider: React.FC<CalendarStateProviderProps> = ({
    children,
    auswahlIsoliert = false,
    initialCalendars,
    initialDate,
    initialView,
    eventTop,
}) => {
    const {
        selectedCalendars: globaleAuswahl,
        toggleCalendar: globalToggle,
        calendarColor,
        mailboxes,
        calendarMailboxes,
        currentMailbox,
    } = useMailContext();

    // T-69: Kalender kommen aus der KALENDER-Liste (Grants, Kollegen nach
    // Outlook-Freigabe, Räume). Die Mail-Liste bleibt als Rückfall, falls die
    // Kalender-Liste (noch) nicht geladen ist — ein Grant steht in beiden.
    const lesbareKalender = useMemo(() => {
        const nachAdresse = new Map<string, MailboxAccess>();
        for (const m of [...(mailboxes ?? []), ...(calendarMailboxes ?? [])]) {
            nachAdresse.set(m.mailboxAddress.toLowerCase(), m);
        }
        return nachAdresse;
    }, [mailboxes, calendarMailboxes]);
    const { subscribe: subscribeRealtime } = useRealtime();

    // Isolierte Auswahl: einmalig aus initialCalendars bzw. der globalen
    // Auswahl geseedet und danach unabhängig. `calendarColor` kommt weiter aus
    // dem MailContext — die Farben sollen überall dieselben sein.
    const [lokaleAuswahl, setLokaleAuswahl] = useState<string[]>(
        () => initialCalendars ?? globaleAuswahl,
    );
    const selectedCalendars = auswahlIsoliert ? lokaleAuswahl : globaleAuswahl;
    const toggleCalendar = useCallback(
        (address: string) => {
            if (!auswahlIsoliert) {
                globalToggle(address);
                return;
            }
            setLokaleAuswahl((prev) =>
                prev.includes(address) ? prev.filter((a) => a !== address) : [...prev, address],
            );
        },
        [auswahlIsoliert, globalToggle],
    );

    const overlayTop = eventTop ?? CAL_OVERLAY_TOP;
    const overlayTopRef = useRef(overlayTop);
    overlayTopRef.current = overlayTop;

    const [view, setViewState] = useState<CalendarView>(() => initialView ?? readStoredView());
    const [currentDate, setCurrentDate] = useState<Date>(() => initialDate ?? new Date());
    const [filter, setFilterState] = useState<CalendarFilterState>(() => readStoredFilter());
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [shared, setShared] = useState(false);
    const [zeitskala, setZeitskalaState] = useState<Zeitskala>(() => readStoredZeitskala());

    // --- Event-Load-State (SP5 füllt die in SP1 reservierten Context-Felder) ---
    // ROH = ungededupt (Review-Fix zu #784): die geteilte Spalten-Ansicht braucht
    // jedes Postfach vollständig; der Overlay-Dedupe läuft als Ableitung (unten).
    const [rawEvents, setRawEvents] = useState<CalendarGridEvent[]>([]);
    // Spiegelt rawEvents in einen Ref, damit der stabile reloadEvents-Callback
    // (leere Deps) beim Load entscheiden kann, ob schon Events sichtbar sind
    // (→ Revalidierung statt Skeleton), ohne selbst neu erzeugt zu werden.
    const rawEventsRef = useRef<CalendarGridEvent[]>([]);
    rawEventsRef.current = rawEvents;
    const [metaById, setMetaById] = useState<Record<string, CalendarMeta>>({});
    // SP4: Erst-Load (kalt, leeres Grid → Skeleton) vs. Revalidierung
    // (Cache-Treffer/sichtbare Events → nur dezenter Hinweis, kein Grid-Leeren).
    const [initialLoading, setInitialLoading] = useState(false);
    const [revalidating, setRevalidating] = useState(false);

    // View persistieren — im isolierten Fenster-Modus BEWUSST nicht: eine
    // Monatsansicht im Verknüpfen-Fenster darf die Seite /termine nicht umstellen.
    const setView = useCallback(
        (v: CalendarView) => {
            setViewState(v);
            if (auswahlIsoliert) return;
            try {
                localStorage.setItem(CAL_VIEW_KEY, v);
            } catch {
                // ignore
            }
        },
        [auswahlIsoliert],
    );

    // Zeitskala persistieren — anders als die View AUCH im isolierten
    // Fenster-Modus: sie ist eine nutzerweite Ansichtseinstellung und wird nur
    // bewusst übers Zahnrad geändert (T-31).
    const setZeitskala = useCallback((z: Zeitskala) => {
        setZeitskalaState(z);
        try {
            localStorage.setItem(ZEITSKALA_KEY, String(z));
        } catch {
            // ignore
        }
    }, []);

    // Filter persistieren.
    const setFilter = useCallback((f: CalendarFilterState) => {
        setFilterState(f);
        try {
            localStorage.setItem(CAL_FILTER_KEY, JSON.stringify(f));
        } catch {
            // ignore
        }
    }, []);

    const toggleFilter = useCallback(
        (key: keyof CalendarFilterState) => {
            setFilter({ ...filter, [key]: !filter[key] });
        },
        [filter, setFilter],
    );

    // Suche debouncen (300ms) — SP5 nutzt debouncedSearch als $search-Param.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Navigation: Schrittweite folgt der aktuellen View. currentDate bleibt beim
    // View-Wechsel erhalten (kein Reset).
    const goToToday = useCallback(() => setCurrentDate(new Date()), []);
    const goPrev = useCallback(() => {
        setCurrentDate((d) => moment(d).subtract(1, unitForView(view)).toDate());
    }, [view]);
    const goNext = useCallback(() => {
        setCurrentDate((d) => moment(d).add(1, unitForView(view)).toDate());
    }, [view]);

    const visibleRange = useMemo(
        () => computeVisibleRange(view, currentDate),
        [view, currentDate],
    );

    // --- Kalenderquellen (Auswahl × Farbe) — Portierung OutlookTermine.tsx:364-373.
    // Filtert gegen die noch zugänglichen Postfächer (eine abgewählte/entzogene
    // Mailbox fällt heraus; listEventsForMailboxes ist via allSettled ohnehin
    // fehlertolerant).
    const calendarSources = useMemo<CalendarSource[]>(
        () =>
            selectedCalendars
                .filter((address) => lesbareKalender.has(address.toLowerCase()))
                .map((address) => {
                    const mb = lesbareKalender.get(address.toLowerCase());
                    return {
                        address,
                        displayName: mb?.displayName || address,
                        color: calendarColor(address),
                    };
                }),
        [selectedCalendars, lesbareKalender, calendarColor],
    );

    // Gewinner-Rang für den Overlay-Dedupe (SP3, Entscheidung 62): eigenes
    // Postfach (currentMailbox) vor WRITE-Postfach vor Rest — der Gewinner-Block
    // bleibt damit im Kalender mit Schreibrecht (Drag/Edit funktioniert).
    const rankOfMailbox = useCallback(
        (address: string): number => {
            const lower = address.toLowerCase();
            if (currentMailbox && lower === currentMailbox.toLowerCase()) return 0;
            const mb = lesbareKalender.get(lower);
            return mb?.calendarAccess === "WRITE" ? 1 : 2;
        },
        [currentMailbox, lesbareKalender],
    );

    // --- Event-Load: SP5 füllt die in SP1 reservierten Felder (Review A3/A4).
    // Pipeline portiert aus OutlookTermine.tsx:375-431 (listEventsForMailboxes +
    // metaBatch-Anreicherung). visibleRange aus dem Context steuert den Range.
    // requestReload (für SP2, out-of-tree) zeigt per Ref stabil auf den aktuellen
    // reloadEvents-Body — EINE Reload-Quelle, vom Context kontrolliert.
    const reloadRef = useRef<{
        from: Date;
        to: Date;
        sources: CalendarSource[];
        kontaktTermine: boolean;
    }>({
        from: visibleRange.from,
        to: visibleRange.to,
        sources: calendarSources,
        kontaktTermine: filter.kontaktTermine,
    });
    reloadRef.current = {
        from: visibleRange.from,
        to: visibleRange.to,
        sources: calendarSources,
        kontaktTermine: filter.kontaktTermine,
    };

    const reloadEvents = useCallback(async () => {
        const { from, to, sources, kontaktTermine } = reloadRef.current;
        if (sources.length === 0 && !kontaktTermine) {
            setRawEvents([]);
            setMetaById({});
            return;
        }
        // Erst-Load vs. Revalidierung (SP4): warm, wenn schon Events sichtbar sind
        // ODER der Range mind. eines Postfachs im (Memory-)Cache liegt — dann kommt
        // der SWR-Treffer sofort, der Refresh läuft im Hintergrund → nur dezenter
        // Hinweis, KEIN Skeleton. Sonst kalter Erst-Load → Skeleton im Grid.
        const warm =
            rawEventsRef.current.length > 0 ||
            sources.some(
                (s) => peekCalendarEvents(s.address, from, to, overlayTopRef.current) !== undefined,
            );
        if (warm) setRevalidating(true);
        else setInitialLoading(true);
        try {
            // Ebene „Termine meiner Kontakte" parallel zum Postfach-Load. Ein
            // Fehler dort darf die eigenen Kalender nicht leeren — deshalb
            // eigener catch, Ergebnis dann einfach leer.
            const kontaktLoad: Promise<KontaktTerminEvent[]> = kontaktTermine
                ? calendarService.listKontaktTermine(from, to).catch((err) => {
                      console.warn("[CalendarStateContext] Termine meiner Kontakte fehlgeschlagen", err);
                      return [];
                  })
                : Promise.resolve([]);
            const [list, kontaktListe] = await Promise.all([
                sources.length > 0
                    ? calendarService.listEventsForMailboxes(sources, from, to, overlayTopRef.current)
                    : Promise.resolve([] as SourcedCalendarEvent[]),
                kontaktLoad,
            ]);
            const roh: CalendarGridEvent[] = list
                .filter((ev: SourcedCalendarEvent) => !istGeburtstagEvent(ev.subject, ev.isAllDay || false))
                .map((ev: SourcedCalendarEvent) => ({
                    id: ev.id,
                    title: ev.subject,
                    // Ganztagstermine liefert Graph als Mitternacht UTC → NUR den
                    // Datumsteil als LOKALE Mitternacht nehmen (graphAllDayDate),
                    // sonst spannt die native All-Day-Zeile einen Tag zu weit (SP3).
                    start: ev.isAllDay
                        ? graphAllDayDate(ev.start.dateTime)
                        : toGraphDate(ev.start.dateTime),
                    end: ev.isAllDay ? graphAllDayDate(ev.end.dateTime) : toGraphDate(ev.end.dateTime),
                    allDay: ev.isAllDay || false,
                    location: ev.location || "",
                    mailbox: ev.source.address,
                    source: ev.source,
                    type: ev.type,
                    seriesMasterId: ev.seriesMasterId,
                    // SP2 (Grid-Lesbarkeit): Tentative-Strichel-Stil + Teams-Icon
                    // brauchen diese beiden List-DTO-Felder im Grid-Event.
                    showAs: ev.showAs ?? null,
                    sensitivity: ev.sensitivity ?? null,
                    onlineMeetingUrl: ev.onlineMeetingUrl ?? null,
                    // SP3: postfachübergreifende Termin-Identität für den Dedupe.
                    iCalUId: ev.iCalUId ?? null,
                    // SP4 (Peek-Popover A16): Organisator, Teilnehmer und
                    // Kurzvorschau sind im List-DTO vorhanden — reines Mapping.
                    organizer: ev.organizer ?? null,
                    attendees: ev.attendees ?? [],
                    bodyPreview: ev.bodyPreview ?? "",
                }));
            // Kontakt-Ebene: gleiche Abbildung, eigene dezente Quellfarbe, und
            // die Meta kommt direkt aus der Antwort (der Batch-Lookup unten
            // überspringt Postfächer ohne Leserecht).
            const kontaktRoh: CalendarGridEvent[] = kontaktListe
                .filter((ev) => !istGeburtstagEvent(ev.subject, ev.isAllDay || false))
                .map((ev) => ({
                    id: ev.id,
                    title: ev.subject,
                    start: ev.isAllDay
                        ? graphAllDayDate(ev.start.dateTime)
                        : toGraphDate(ev.start.dateTime),
                    end: ev.isAllDay ? graphAllDayDate(ev.end.dateTime) : toGraphDate(ev.end.dateTime),
                    allDay: ev.isAllDay || false,
                    location: ev.location || "",
                    mailbox: ev.source.address,
                    source: {
                        address: ev.source.address,
                        displayName: `über ${ev.ueberKontakt.name}`,
                        color: KONTAKT_EBENE_FARBE,
                    },
                    type: ev.type,
                    seriesMasterId: ev.seriesMasterId,
                    showAs: ev.showAs ?? null,
                    sensitivity: ev.sensitivity ?? null,
                    onlineMeetingUrl: ev.onlineMeetingUrl ?? null,
                    iCalUId: ev.iCalUId ?? null,
                    organizer: ev.organizer ?? null,
                    attendees: ev.attendees ?? [],
                    bodyPreview: ev.bodyPreview ?? "",
                    ueberKontakt: ev.ueberKontakt,
                    meta: ev.kmvs
                        ? {
                              terminArt: ev.kmvs.terminArt,
                              kundeId: null,
                              farbe: ev.kmvs.farbe ?? null,
                              vorgangId: ev.kmvs.vorgangId ?? null,
                              vorgangTitel: ev.kmvs.vorgangTitel ?? null,
                          }
                        : undefined,
                }));
            // KEIN Dedupe hier (Review-Fix zu #784): der State hält die
            // UNgededupten Events — die geteilte Spalten-Ansicht zeigt jedes
            // Postfach vollständig. Der Overlay-Dedupe (SP3, Entscheidung 62)
            // läuft als Ableitung in deriveViewEvents (unten).
            roh.sort((a, b) => a.start.getTime() - b.start.getTime());

            // Sidecar-Meta in EINEM Batch nachladen (N+1-frei) — über die ROHEN
            // Events inkl. Dedupe-Verlierer: die Spalten-Ansicht zeigt die
            // Verlierer und braucht deren Klassifikation. Best-effort: schlägt
            // der Batch fehl, bleiben Events unklassifiziert (= austausch).
            const nextMeta: Record<string, CalendarMeta> = {};
            for (const e of kontaktRoh) {
                if (e.meta) nextMeta[metaKey(e.mailbox, e.id)] = e.meta;
            }
            try {
                const metas = await calendarTerminService.metaBatch(
                    roh.map((e) => ({ mailbox: e.mailbox, graphEventId: e.id })),
                );
                for (const m of metas) {
                    nextMeta[metaKey(m.mailbox, m.graphEventId)] = {
                        terminArt: m.terminArt,
                        blockTyp: m.blockTyp ?? null,
                        pflichtBlock: m.pflichtBlock ?? false,
                        abwesenheitTyp: m.abwesenheitTyp ?? null,
                        kundeId: m.kundeId ?? null,
                        // SP6/A10: individuelle Farbe (schlägt die Art-Farbe im Grid).
                        farbe: m.farbe ?? null,
                        // K9: der Vorgang am Termin — Kennzeichnung, Tooltip, Filter.
                        vorgangId: m.vorgangId ?? null,
                        vorgangTitel: m.vorgangTitel ?? null,
                    };
                }
                for (const e of roh) {
                    e.meta = nextMeta[metaKey(e.mailbox, e.id)];
                }
            } catch (metaErr) {
                console.warn("[CalendarStateContext] Art-Batch fehlgeschlagen", metaErr);
            }

            // Kontakt-Termine NACH dem Batch anhängen (sie tragen ihre Meta
            // schon); der Overlay-Dedupe (iCalUId) räumt Dubletten ab.
            const alle = [...roh, ...kontaktRoh].sort(
                (a, b) => a.start.getTime() - b.start.getTime(),
            );
            setRawEvents(alle);
            setMetaById(nextMeta);
        } catch (err) {
            console.error("[CalendarStateContext] Fehler beim Laden der Termine", err);
        } finally {
            setInitialLoading(false);
            setRevalidating(false);
        }
    }, []);

    const requestReload = useRef(reloadEvents);
    requestReload.current = reloadEvents;
    const requestReloadStable = useCallback(() => {
        void requestReload.current();
    }, []);

    // Reload bei Änderung von Auswahl oder sichtbarem Range.
    useEffect(() => {
        void reloadEvents();
    }, [reloadEvents, calendarSources, visibleRange, filter.kontaktTermine]);

    // Out-of-tree-Aufrufer (TerminFormWindow im WindowLayer) abonnieren: ihr
    // requestCalendarReload() landet hier beim aktuellen reloadEvents. EINE
    // Reload-Quelle, vom Context kontrolliert (A4).
    useEffect(() => {
        const fn = () => {
            void requestReload.current();
        };
        reloadSubscribers.add(fn);
        return () => {
            reloadSubscribers.delete(fn);
        };
    }, []);

    // Fokus-/Wake-Revalidierung (SP4): an das synthetische 'reconnect'-Event des
    // RealtimeContext (Mail-SP3, feuert bei Tab-Rückkehr/Wake nach Ausfall) hängen
    // — KEIN eigener visibilitychange/focus-Listener mit eigener Schwelle. Die
    // sichtbaren Ranges der gewählten Postfächer verwerfen und neu laden; SWR hält
    // wiederholte Navigation ohnehin billig. Während des Ausfalls verpasste
    // Fremd-Änderungen holt so der nächste Blick auf.
    useEffect(() => {
        const unsub = subscribeRealtime("reconnect", () => {
            for (const s of reloadRef.current.sources) {
                invalidateCalendarMailbox(s.address);
            }
            void requestReload.current();
        });
        return unsub;
    }, [subscribeRealtime]);

    // Echtzeit-Push (Termine-SP8): eine Graph-Kalender-Notification kommt als
    // metadatenarmes 'calendar'-Frame `{ mailbox }` an. Nur betroffene, AKTUELL
    // ausgewählte Postfächer werden invalidiert + neu geladen — Fremd-/abgewählte
    // Postfächer ignorieren wir (kein unnötiger Graph-Fetch). Mehrere Frames in
    // kurzer Folge (Graph bündelt Änderungen) werden zu EINEM Reload zusammen-
    // gefasst (Throttle); SWR hält den Reload ohnehin billig. Fällt der Webhook
    // aus, bleibt die Fokus-/Wake-Revalidierung (reconnect, oben) das Fallback.
    const calendarReloadTimer = useRef<number | null>(null);
    useEffect(() => {
        const unsub = subscribeRealtime("calendar", (evt) => {
            const mailbox = (evt.payload as CalendarRealtimePayload | undefined)?.mailbox;
            if (!mailbox) return;
            const lower = mailbox.toLowerCase();
            const affected = reloadRef.current.sources.some(
                (s) => s.address.toLowerCase() === lower,
            );
            if (!affected) return;
            // Cache des betroffenen Postfachs verwerfen, damit der Reload frisch lädt.
            invalidateCalendarMailbox(mailbox);
            // Coalesced Reload (Throttle) — bündelt Notification-Bursts zu EINEM Load.
            if (calendarReloadTimer.current != null) return;
            calendarReloadTimer.current = window.setTimeout(() => {
                calendarReloadTimer.current = null;
                void requestReload.current();
            }, 1200);
        });
        return () => {
            unsub();
            if (calendarReloadTimer.current != null) {
                window.clearTimeout(calendarReloadTimer.current);
                calendarReloadTimer.current = null;
            }
        };
    }, [subscribeRealtime]);

    // Sichtbarkeit unter dem Art-Filter (kundentermin/austausch) — block/
    // abwesenheit bleiben immer sichtbar (D2-Helper aus types.ts, keine Duplikation).
    const istSichtbar = useCallback(
        (e: CalendarGridEvent) => isVisibleUnderFilter(classifyTermin(e.meta), filter, e.meta),
        [filter],
    );

    // Ansichten ableiten (Review-Fix zu #784): der Overlay-Dedupe (Entscheidung
    // 62) gilt NUR für die Overlay-/Einzelansicht (events/filteredEvents); die
    // geteilte Spalten-Ansicht bekommt mit filteredColumnEvents die UNgededupten
    // Events — jede Spalte zeigt ihr Postfach vollständig.
    const { events, filteredEvents, filteredColumnEvents } = useMemo(
        () => deriveViewEvents(rawEvents, istSichtbar, rankOfMailbox),
        [rawEvents, istSichtbar, rankOfMailbox],
    );

    const value = useMemo<CalendarStateContextValue>(
        () => ({
            view,
            setView,
            currentDate,
            setCurrentDate,
            goToToday,
            goPrev,
            goNext,
            filter,
            setFilter,
            toggleFilter,
            search,
            setSearch,
            debouncedSearch,
            shared,
            setShared,
            zeitskala,
            setZeitskala,
            selectedCalendars,
            toggleCalendar,
            calendarColor,
            visibleRange,
            // SP5 (gefüllt):
            events,
            filteredEvents,
            filteredColumnEvents,
            metaById,
            loadingEvents: initialLoading || revalidating,
            initialLoading,
            revalidating,
            reloadEvents,
            requestReload: requestReloadStable,
            calendarSources,
        }),
        [
            view,
            setView,
            currentDate,
            goToToday,
            goPrev,
            goNext,
            filter,
            setFilter,
            toggleFilter,
            search,
            debouncedSearch,
            shared,
            zeitskala,
            setZeitskala,
            selectedCalendars,
            toggleCalendar,
            calendarColor,
            visibleRange,
            events,
            filteredEvents,
            filteredColumnEvents,
            metaById,
            initialLoading,
            revalidating,
            reloadEvents,
            requestReloadStable,
            calendarSources,
        ],
    );

    return (
        <CalendarStateContext.Provider value={value}>{children}</CalendarStateContext.Provider>
    );
};

export function useCalendarState(): CalendarStateContextValue {
    const ctx = useContext(CalendarStateContext);
    if (ctx === undefined) {
        throw new Error("useCalendarState must be used within a CalendarStateProvider");
    }
    return ctx;
}
